import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { SpotlightLoading } from "@/components/spotlight/SpotlightLoading";
import { SpotlightHero } from "@/components/spotlight/SpotlightHero";
import { SpotlightHomesCarousel } from "@/components/spotlight/SpotlightHomesCarousel";
import { SpotlightTeam } from "@/components/spotlight/SpotlightTeam";
import { SpotlightCTA } from "@/components/spotlight/SpotlightCTA";
import { SpotlightMarketComparison } from "@/components/spotlight/SpotlightMarketComparison";
import { SpotlightDigitalFootprint } from "@/components/spotlight/SpotlightDigitalFootprint";
import { SpotlightMap } from "@/components/spotlight/SpotlightMap";
import { SpotlightShowings } from "@/components/spotlight/SpotlightShowings";
import { SpotlightReviews } from "@/components/spotlight/SpotlightReviews";
import { SpotlightSocialLinks } from "@/components/spotlight/SpotlightSocialLinks";
import { SpotlightSpeedToSold } from "@/components/spotlight/SpotlightSpeedToSold";
import { SpotlightIconPattern } from "@/components/spotlight/SpotlightIconPattern";
import NotFound from "@/pages/NotFound";
import northLogo from "@/assets/north-logo.png";

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Generates a data-driven headline for the hero when the user hasn't set one.
 */
function buildAiHeadline(
  teamName: string,
  stats: { avg_dom: number },
  settings: { zillow_sales_count_all_time: number | null; zillow_sales_count_last_year: number | null; custom_avg_dom: number | null },
  market: { market_median_dom: number } | null,
): string {
  const teamDom = settings.custom_avg_dom ?? stats.avg_dom;
  const marketDom = market?.market_median_dom ?? 0;

  // Lead with speed advantage if significant
  if (teamDom > 0 && marketDom > 0 && teamDom < marketDom) {
    const pct = Math.round(((marketDom - teamDom) / marketDom) * 100);
    if (pct >= 20) {
      return `Selling ${pct}% Faster Than the Market`;
    }
  }

  const career = settings.zillow_sales_count_all_time;
  const lastYear = settings.zillow_sales_count_last_year;

  if (career && career >= 1000) {
    return `${career.toLocaleString()}+ Homes Sold and Counting`;
  }
  if (career && career >= 100) {
    return `${career.toLocaleString()} Homes Sold and We're Just Getting Started`;
  }
  if (lastYear && lastYear >= 20) {
    return `${lastYear} Homes Sold This Year Alone`;
  }
  return `Your Trusted Real Estate Partner`;
}

/**
 * Generates a data-driven tagline for the hero section.
 * Picks the most impressive stat and turns it into a punchy phrase.
 */
function buildAiTagline(
  stats: { total_sold: number; avg_dom: number; total_volume: number; avg_price: number },
  settings: { zillow_sales_count_all_time: number | null; zillow_sales_count_last_year: number | null; subheadline: string | null; headline: string | null; custom_avg_dom: number | null },
  market: { market_median_dom: number; avg_demand_rating: number } | null,
): string | null {
  // If the user wrote their own subheadline, respect it
  if (settings.subheadline) return null;

  const fragments: string[] = [];

  const career = settings.zillow_sales_count_all_time;
  const lastYear = settings.zillow_sales_count_last_year;
  const teamDom = settings.custom_avg_dom ?? stats.avg_dom;
  const marketDom = market?.market_median_dom ?? 0;
  const headlineIsAi = !settings.headline;
  const headlineUsedSpeed = headlineIsAi && teamDom > 0 && marketDom > 0 && teamDom < marketDom
    && Math.round(((marketDom - teamDom) / marketDom) * 100) >= 20;

  // Career milestone (lead with this since headline likely covers speed)
  if (career && career >= 1000) {
    fragments.push(`${career.toLocaleString()}+ homes sold and counting`);
  } else if (career && career >= 100) {
    fragments.push(`${career.toLocaleString()} homes sold`);
  }

  // Speed advantage (only if headline didn't already use it)
  if (!headlineUsedSpeed && teamDom > 0 && marketDom > 0 && teamDom < marketDom) {
    const pct = Math.round(((marketDom - teamDom) / marketDom) * 100);
    if (pct >= 20) {
      fragments.push(`selling ${pct}% faster than the market`);
    }
  }

  // Recent volume
  if (lastYear && lastYear >= 50) {
    fragments.push(`${lastYear} transactions in the last year`);
  }

  if (fragments.length === 0) return null;

  const first = fragments[0].charAt(0).toUpperCase() + fragments[0].slice(1);
  if (fragments.length === 1) return first + ".";
  return fragments.map((f, i) => i === 0 ? f.charAt(0).toUpperCase() + f.slice(1) : f).join(", ") + ".";
}

interface SpotlightData {
  settings: {
    headline: string;
    subheadline: string | null;
    cta_text: string;
    cta_link: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    show_homes_carousel: boolean;
    show_sales_stats: boolean;
    show_team_section: boolean;
    show_views_saves: boolean;
    show_market_comparison: boolean;
    show_map: boolean;
    show_showings: boolean;
    show_reviews: boolean;
    show_speed_to_sold: boolean;
    zillow_sales_count_all_time: number | null;
    zillow_sales_count_last_year: number | null;
    zillow_avg_value_three_year: number | null;
    zillow_price_range_min: number | null;
    zillow_price_range_max: number | null;
    custom_primary_color: string | null;
    custom_secondary_color: string | null;
    social_facebook: string | null;
    social_instagram: string | null;
    social_linkedin: string | null;
    social_twitter: string | null;
    social_youtube: string | null;
    social_tiktok: string | null;
    review_summary: string | null;
    zillow_total_reviews: number | null;
    page_views_count: number;
    custom_avg_dom: number | null;
    hero_media_type: string | null;
    hero_custom_url: string | null;
    hero_listing_north_id: string | null;
    cta_listing_north_id: string | null;
  };
  team: {
    name: string;
    company_name: string;
    company_logo_url: string;
    default_primary_color: string | null;
    default_secondary_color: string | null;
  };
  agents: Array<{
    name: string;
    headshot_url: string | null;
  }>;
  homes: Array<{
    id: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    price: number | null;
    beds: number | null;
    baths: number | null;
    sqft: number | null;
    image_url: string;
    listing_status: string | null;
    days_on_zillow: number | null;
    views_count: number;
    saves_count: number;
    report_views: number;
    report_saves: number;
    portal_views: number;
    portal_saves: number;
    showings_count: number;
    custom_agent_name: string | null;
    agent_photo_url: string | null;
  }>;
  stats: {
    total_listings: number;
    total_sold: number;
    total_active: number;
    avg_dom: number;
    total_volume: number;
    avg_price: number;
    total_views: number;
    total_saves: number;
    total_zillow_views: number;
    total_zillow_saves: number;
    total_realtor_views: number;
    total_realtor_saves: number;
    total_homes_views: number;
    total_homes_saves: number;
    total_redfin_views: number;
    total_redfin_saves: number;
    total_trulia_views: number;
    total_trulia_saves: number;
    total_portal_views: number;
    total_portal_saves: number;
    total_showings: number;
    highest_sale: number;
    last_year_sold: number;
  };
  market: {
    market_median_dom: number;
    market_active_listings: number;
    market_sold_listings: number;
    avg_demand_rating: number;
    neighborhoods_with_data: number;
    market_price_reduction_rate: number;
    team_price_reduction_rate: number;
  };
  showings: Array<{
    scheduled_date: string;
    event_type: string;
    attendees_count: number;
  }>;
  reviews: Array<{
    reviewer_name: string;
    rating: number;
    review_text: string | null;
    review_date: string | null;
    reviewer_badge: string | null;
  }>;
  sold_listings: Array<{
    street_address: string;
    city: string | null;
    state: string | null;
    zip: string | null;
    latitude: number | null;
    longitude: number | null;
    price: string | null;
    sold_date: string | null;
    bedrooms: number | null;
    bathrooms: number | null;
    living_area: number | null;
    represented: string | null;
    image_url: string | null;
  }>;
  view_sources?: Record<string, number>;
}

export default function Spotlight() {
  const { teamId, slug } = useParams<{ teamId?: string; slug?: string }>();

  // Determine lookup mode: UUID or slug
  const isUuidMode = !!teamId && uuidRegex.test(teamId);
  const isSlugMode = !!slug && slug.length > 0;
  const isValid = isUuidMode || isSlugMode;

  const { data, isLoading, error } = useQuery<SpotlightData | null>({
    queryKey: ["spotlight", teamId || slug],
    queryFn: async () => {
      let result, err;
      if (isUuidMode) {
        ({ data: result, error: err } = await supabase.rpc("get_spotlight_data", {
          team_id_param: teamId!,
        }));
      } else if (isSlugMode) {
        ({ data: result, error: err } = await supabase.rpc("get_spotlight_data_by_slug", {
          slug_param: slug!,
        }));
      } else {
        return null;
      }
      if (err) throw err;
      // RPC may return array-wrapped JSONB — unwrap if needed
      if (Array.isArray(result)) {
        result = result[0] ?? null;
      }
      return result as SpotlightData | null;
    },
    enabled: isValid,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
  });

  // Track page views (deferred to avoid blocking render)
  useEffect(() => {
    if (!data || !isValid || isLoading) return;

    const trackView = async () => {
      try {
        // For slug mode, we need to extract team_id from the settings data
        const resolvedTeamId = isUuidMode ? teamId! : (data.settings as any)?.team_id;
        if (resolvedTeamId) {
          await supabase.rpc("increment_spotlight_views", {
            team_id_param: resolvedTeamId,
          });
        }
      } catch (err) {
        logger.error("Error tracking spotlight view:", err);
      }
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      requestIdleCallback(trackView, { timeout: 2000 });
    } else {
      setTimeout(trackView, 100);
    }
  }, [data, teamId, slug, isValid, isUuidMode, isLoading]);

  if (!isValid) return <NotFound />;
  if (isLoading) return <SpotlightLoading />;
  if (error || !data) return <NotFound />;

  const { settings, team, agents, homes, stats, market, showings, reviews, sold_listings, view_sources } = data;

  // Resolve colors: custom overrides > team defaults
  const primaryColor = settings.custom_primary_color || team.default_primary_color;
  const secondaryColor = settings.custom_secondary_color || team.default_secondary_color;

  const hasSocialLinks =
    settings.social_facebook ||
    settings.social_instagram ||
    settings.social_linkedin ||
    settings.social_twitter ||
    settings.social_youtube ||
    settings.social_tiktok;

  // Resolve hero media
  const heroMediaType = settings.hero_media_type ?? "listing";
  const selectedNorth = settings.hero_listing_north_id
    ? homes.find((h) => h.id === settings.hero_listing_north_id)
    : null;
  const listingImageUrl = selectedNorth?.image_url
    ?? (homes.length > 0 ? homes[0].image_url : null);
  const heroImageUrl = heroMediaType === "photo" && settings.hero_custom_url
    ? settings.hero_custom_url
    : heroMediaType === "listing"
      ? listingImageUrl
      : null;
  const heroVideoUrl = heroMediaType === "video" && settings.hero_custom_url
    ? settings.hero_custom_url
    : null;

  // Build AI-generated headline and tagline from data
  const aiHeadline = buildAiHeadline(team.name, stats, settings, market);
  const aiTagline = buildAiTagline(stats, settings, market);

  return (
    <div className="min-h-screen">
      <SpotlightHero
        teamName={team.name}
        companyLogoUrl={team.company_logo_url}
        headline={settings.headline || aiHeadline}
        subheadline={aiTagline || settings.subheadline}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        heroImageUrl={heroImageUrl}
        heroVideoUrl={heroVideoUrl}
      />

      {settings.show_speed_to_sold && settings.zillow_sales_count_all_time && (
        <SpotlightSpeedToSold
          salesCountAllTime={settings.zillow_sales_count_all_time}
          salesCountLastYear={settings.zillow_sales_count_last_year}
          avgValueThreeYear={settings.zillow_avg_value_three_year}
          priceRangeMin={settings.zillow_price_range_min}
          priceRangeMax={settings.zillow_price_range_max}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_views_saves && (
        <SpotlightDigitalFootprint
          portalViews={stats.total_portal_views}
          portalSaves={stats.total_portal_saves}
          totalViews={stats.total_views}
          totalSaves={stats.total_saves}
          homes={homes}
          primaryColor={primaryColor}
          viewSources={view_sources}
        />
      )}

      {settings.show_market_comparison && market && (
        <SpotlightMarketComparison
          market={market}
          teamAvgDom={settings.custom_avg_dom ?? stats.avg_dom}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_homes_carousel && homes.length > 0 && (
        <SpotlightHomesCarousel
          homes={homes}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_map && sold_listings && sold_listings.length > 0 && (
        <SpotlightMap
          soldListings={sold_listings}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_showings && showings && showings.length > 0 && (
        <SpotlightShowings
          showings={showings}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_reviews && reviews && reviews.length > 0 && (
        <SpotlightReviews
          reviews={reviews}
          reviewSummary={settings.review_summary}
          totalReviewCount={settings.zillow_total_reviews}
          primaryColor={primaryColor}
        />
      )}

      {settings.show_team_section && agents.length > 0 && (
        <SpotlightTeam
          agents={agents}
          teamName={team.name}
          primaryColor={primaryColor}
        />
      )}

      <SpotlightCTA
        ctaText={settings.cta_text}
        ctaLink={settings.cta_link}
        contactEmail={settings.contact_email}
        contactPhone={settings.contact_phone}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        featuredImageUrl={
          settings.cta_listing_north_id
            ? (homes.find((h) => h.id === settings.cta_listing_north_id)?.image_url ?? null)
            : ([...homes].sort((a, b) => (b.price ?? 0) - (a.price ?? 0)).find(
                (h) => h.listing_status && /active|for.sale/i.test(h.listing_status)
              )?.image_url ?? homes[0]?.image_url ?? null)
        }
      />

      <footer className="py-8 px-6 text-center bg-[#f8fafc] border-t border-slate-100">
        {hasSocialLinks && <SpotlightSocialLinks links={settings} primaryColor={primaryColor} />}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          <span className="text-xs text-slate-400">Powered by</span>
          <img src={northLogo} alt="North Reports" className="h-4" />
        </div>
      </footer>
    </div>
  );
}
